import {
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";

import { adminFaxApi } from "apis";
import {
  FaxProfileResponseDto,
  FaxProfileResponseDtoStatusEnum,
  ResponseError,
  UpdateFaxProfileStatusDtoStatusEnum,
  UpsertFaxProfileDto,
} from "libs/cellera-sdk";
import { useToast } from "contexts/ToastContext";

const queryKey = (practiceId: string) => ["fax-profile", practiceId] as const;

export type FaxProfileDto = FaxProfileResponseDto;
export type FaxProfileStatus = FaxProfileResponseDtoStatusEnum;
export type UpdateFaxProfileStatus = UpdateFaxProfileStatusDtoStatusEnum;
export type UpsertFaxProfileRequest = UpsertFaxProfileDto;

interface ToastCopy {
  title: string;
  body: string;
}

const FAX_ERROR_COPY: Record<string, ToastCopy> = {
  ERA_0155: {
    title: "Fax profile not found",
    body: "The profile no longer exists. Please refresh.",
  },
  ERA_0156: {
    title: "Action not allowed",
    body: "This profile state cannot be changed.",
  },
  ERA_0157: {
    title: "Sinch validation failed",
    body: "Confirm the number is assigned to the service and that both inbound and outbound document storage are disabled.",
  },
  ERA_0158: {
    title: "Fax number unavailable",
    body: "This fax number is already used by another practice.",
  },
  ERA_0159: {
    title: "Sinch unavailable",
    body: "Sinch is temporarily unavailable. Please retry in a moment.",
  },
};

interface BackendErrorBody {
  errorCode?: string;
  message?: string;
  messages?: string[];
}

const getBackendErrorBody = async (
  error: unknown,
): Promise<BackendErrorBody | undefined> => {
  if (!(error instanceof ResponseError)) return undefined;
  try {
    return (await error.response.clone().json()) as BackendErrorBody;
  } catch {
    return undefined;
  }
};

const resolveErrorCopy = async (error: unknown): Promise<ToastCopy> => {
  const body = await getBackendErrorBody(error);
  if (body?.errorCode) {
    const mapped = FAX_ERROR_COPY[body.errorCode];
    if (mapped) return mapped;
  }
  const fallbackBody =
    body?.message ??
    body?.messages?.join(", ") ??
    (error instanceof Error && error.message
      ? error.message
      : "Please try again.");
  return { title: "Something went wrong", body: fallbackBody };
};

export const useFaxProfileQuery = (
  practiceId: string,
): UseQueryResult<FaxProfileDto | null, ResponseError> =>
  useQuery({
    queryKey: queryKey(practiceId),
    queryFn: async () => {
      try {
        return await adminFaxApi.getFaxProfile({ practiceId });
      } catch (error) {
        if (error instanceof ResponseError && error.response.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(practiceId),
    staleTime: 30_000,
  });

export const useUpsertFaxProfile = (
  practiceId: string,
): UseMutationResult<FaxProfileDto, ResponseError, UpsertFaxProfileRequest> => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: (body: UpsertFaxProfileRequest) =>
      adminFaxApi.upsertFaxProfile({
        practiceId,
        upsertFaxProfileDto: body,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey(practiceId), data);
      queryClient.invalidateQueries({ queryKey: queryKey(practiceId) });
      showSuccess("Fax profile saved.");
    },
    onError: async (error) => {
      const copy = await resolveErrorCopy(error);
      showError(copy.title, copy.body);
    },
  });
};

export const useUpdateFaxProfileStatus = (
  practiceId: string,
): UseMutationResult<FaxProfileDto, ResponseError, UpdateFaxProfileStatus> => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const successTitle = (status: UpdateFaxProfileStatus): string => {
    switch (status) {
      case "active":
        return "Profile reactivated.";
      case "suspended":
        return "Profile suspended.";
      case "retired":
        return "Profile retired.";
      default:
        return "Profile updated.";
    }
  };

  return useMutation({
    mutationFn: (status: UpdateFaxProfileStatus) =>
      adminFaxApi.updateFaxProfileStatus({
        practiceId,
        updateFaxProfileStatusDto: { status },
      }),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKey(practiceId), data);
      queryClient.invalidateQueries({ queryKey: queryKey(practiceId) });
      if (data.status === variables) {
        showSuccess(successTitle(variables));
      } else {
        showError("Action not applied", `Profile remains ${data.status}.`);
      }
    },
    onError: async (error) => {
      const copy = await resolveErrorCopy(error);
      showError(copy.title, copy.body);
    },
  });
};
